'use client'

/** The line under the header that answers a Continue press: the next step, or the server's refusal. */

import { Icons } from '@/components/ui/icons'
import { useStory } from '@/lib/story'
import { clearContinueNotice, storyToken, useContinueNotice, waitKey } from '@/lib/autopilot'

/**
 * The answer to a Continue press from the chip or the stage shortcut, under
 * the header: "Continuing to Posts sensed", or the server's refusal sentence
 * ("WB-001 needs a decision in the Auto Workbench. Continue does not approve
 * anything."). The polite region is always mounted so the first answer is
 * read; a refusal is an alert and stays until it is dismissed or the wait
 * changes. Each notice is keyed, so the same sentence twice is read twice.
 */
export function ContinueNoticeLine() {
  const { story } = useStory()
  const { notice, holdProps } = useContinueNotice('header', waitKey(storyToken(story)))
  const refusal = notice?.tone === 'refusal'
  return (
    <div className='pointer-events-none absolute right-3 top-full mt-2 flex max-w-[min(28rem,calc(100vw-2rem))] flex-col items-end lg:right-5'>
      <div role='status' aria-live='polite' aria-atomic='true'>
        {notice && !refusal && (
          <p
            key={notice.id}
            {...holdProps}
            className='pointer-events-auto flex items-start gap-2 rounded-xl border border-hairline bg-card px-3 py-2 text-xs text-foreground shadow-float'
          >
            <Icons.chevronRight className='mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground' strokeWidth={1.5} aria-hidden='true' />
            {notice.text}
          </p>
        )}
      </div>
      {notice && refusal && (
        <p
          key={notice.id}
          role='alert'
          className='pointer-events-auto flex items-start gap-2 rounded-xl border border-status-at-risk/30 bg-warning-wash px-3 py-2 text-xs text-foreground shadow-float'
        >
          <Icons.info className='mt-0.5 h-3.5 w-3.5 shrink-0 text-status-at-risk' strokeWidth={1.5} aria-hidden='true' />
          {notice.text}
          <button
            type='button'
            onClick={() => clearContinueNotice(notice.id)}
            aria-label='Dismiss'
            className='-my-0.5 -mr-1 ml-1 shrink-0 rounded-full p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            <Icons.close className='h-3.5 w-3.5' strokeWidth={1.5} aria-hidden='true' />
          </button>
        </p>
      )}
    </div>
  )
}
